/* cf-quote-truth.js - Click Fencing, 2026-08-15, delivery normalised 2026-08-20
 *
 * Makes the saved quote agree with Shopify before the customer confirms it, so the quote,
 * the cart and the order are the same numbers and the same products.
 *
 * Five jobs, all on /pages/quote-review, all on the quote the page is showing:
 *
 * 1. RESOLVE VARIANTS the calculators leave null.
 *    - by SKU via CF_VARIANT (mesh and blade posts: 49321/49838/49839/49896/49323...)
 *    - by WIDTH for glass, which names a line "1700mm Glass Panels" and attaches no SKU at
 *      all. Every glass panel, hinge panel, raked panel and gate IS a real variant keyed on
 *      width (frameless-glass-panel has 40, 100-2100mm), so the width in the label is
 *      enough. Before this, $435 of a $1,048 glass order went to the cart as an untitled
 *      balance line.
 *
 * 2. ADOPT THE SHOPIFY PRICE where a resolved line disagrees. Pete's rule since 2026-08-11
 *    is one price, from Shopify, everywhere. The calculators still carry hard-coded price
 *    tables that drift: perfmesh-calc.js quotes the 1900mm raked post (49896) at $59.89
 *    while Shopify sells it at $63.51, and the 2400mm in-ground raked post (49323) at
 *    $67.28 while Shopify sells it at $59.59. The first loses money on every raked mesh
 *    section; the second overcharges. Both are corrected here, in whichever direction the
 *    catalogue says.
 *
 * 3. DROP THE CALCULATOR'S OWN GST LINE. Glass - alone of the four - pushes a "GST (10%)"
 *    line into its breakdown. Removing it makes all four calculators speak one currency so
 *    cf-gst-display.js has one path and GST is counted once.
 *
 * 4. DROP FLAT TOP'S STRAY SPIGOT LINE - $110.30 a quote for parts that are never fitted.
 *    Pete's call, 2026-08-15.
 *
 * 5. NORMALISE THE DELIVERY CHARGE. Added 2026-08-20. Pete: "all delivery orders charge
 *    $90+gst" - one metro rate, no tiers. Glass and blade read it from
 *    shopifyData.settings.deliveryCharge and are correct, but flat top and PerfMesh emit
 *    their own hard-coded "DIY Metro Delivery (orders <$2,500)" at $75. Because
 *    cf-real-line-items.js deliberately keeps delivery OUT of the cart and lets Shopify
 *    apply its rate once (see click-fencing-DELIVERY-DOUBLE-CHARGE-2026-08-18.md), those
 *    customers were SHOWN $75 and CHARGED the Shopify rate - a $24 overcharge per delivered
 *    order once the rate went to $99 inc GST. The stale tier label is rewritten too, since
 *    "orders <$2,500" is no longer a real offer. Pickup lines are $0 and are never touched.
 *
 * Runs before the customer sees a price: it rewrites the stored quote, then re-clicks the
 * active pill so the editor and the GST split rebuild from the corrected figures.
 *
 * Every path wrapped; if anything fails the quote is left exactly as the calculator wrote
 * it and the existing behaviour (including the never-overcharge guard in
 * cf-real-line-items.js) still applies.
 *
 * Load AFTER cf-sku-prices.js (needs CF_PRICE / CF_VARIANT) and after cf-quote-editor.js.
 */
(function () {
  'use strict';
  if (window.__cfQuoteTruthInstalled) return;
  window.__cfQuoteTruthInstalled = true;

  var CALCS = ['ullc', 'blade', 'flattop', 'perfmesh'];

  /* The one Perth metro rate, matching the Shopify shipping rate exactly. $90 + GST, so
     $99 once the store shows GST-inclusive prices and $90 while it does not. */
  var METRO_DELIVERY = (window.CF_TAXES_INCLUDED === true) ? 99 : 90;

  /* Glass products, keyed by the shape of the line label. Plain and Enduroshield are
     separate products at different prices, so the label decides which. */
  var GLASS = [
    { test: /enduroshield/i, kind: /hinge panel/i, handle: 'enduroshield-coated-toughened-glass-hinge-panel' },
    { test: /enduroshield/i, kind: /raked|sloped/i, handle: 'enduroshield-coated-safety-toughened-frameless-glass-raked-panel' },
    { test: /enduroshield/i, kind: /gate/i, handle: 'clear-safety-toughened-frameless-glass-soft-close-hinge-gate-panel' },
    { test: /enduroshield/i, kind: /panel/i, handle: 'enduroshield-clear-safety-toughened-frameless-glass-panel' },
    { test: null, kind: /hinge panel/i, handle: 'glass-pool-fence-hinge-panel' },
    { test: null, kind: /raked|sloped/i, handle: 'clear-safety-toughened-frameless-glass-raked-panel' },
    { test: null, kind: /gate/i, handle: 'toughened-soft-close-hinge-frameless-glass-gate-panel-12mmtx-1200mm' },
    { test: null, kind: /panel/i, handle: 'frameless-glass-panel' }
  ];

  var widthMap = null;   /* handle -> { width -> {id, cents, sku} } */

  function buildWidthMap() {
    if (widthMap) return Promise.resolve(widthMap);
    var handles = {};
    GLASS.forEach(function (g) { handles[g.handle] = 1; });
    var acc = {}, page = 1, all = [];
    function next() {
      return fetch('/products.json?limit=250&page=' + page)
        .then(function (r) { return r.json(); })
        .then(function (d) {
          if (!d || !d.products || !d.products.length || page > 8) return;
          all = all.concat(d.products);
          page++;
          return next();
        });
    }
    return next().then(function () {
      all.forEach(function (p) {
        if (!handles[p.handle]) return;
        var m = acc[p.handle] = {};
        (p.variants || []).forEach(function (v) {
          var w = parseInt(v.title, 10);
          if (!isNaN(w)) m[w] = { id: v.id, cents: Math.round(parseFloat(v.price) * 100), sku: v.sku };
        });
      });
      widthMap = acc;
      return widthMap;
    }).catch(function () { widthMap = {}; return widthMap; });
  }

  function glassLookup(item) {
    var label = String(item || '');
    var w = label.match(/(\d{3,4})\s*mm/);
    if (!w) return null;
    var width = parseInt(w[1], 10);
    var isEndure = /enduroshield/i.test(label);
    for (var i = 0; i < GLASS.length; i++) {
      var g = GLASS[i];
      if (!!g.test !== isEndure) continue;
      if (!g.kind.test(label)) continue;
      var m = widthMap && widthMap[g.handle];
      if (m && m[width]) return m[width];
      return null;   /* right product, no such width - leave it alone rather than guess */
    }
    return null;
  }

  function isTax(b) {
    return /^tax$/i.test(String(b.category || '').trim()) || /\bgst\b/i.test(String(b.item || ''));
  }

  function isNonStock(b) {
    return /delivery|pickup|inspection fee/i.test(String(b.category || '') + ' ' + String(b.item || ''));
  }

  function isPickup(b) {
    return /pick\s*-?\s*up/i.test(String(b.category || '') + ' ' + String(b.item || ''));
  }

  /* A chargeable metro delivery line. Pickup is $0 and is deliberately excluded, as is any
     zero-value line, so free delivery promotions are never turned into a charge. */
  function isMetroDelivery(b) {
    if (!/delivery/i.test(String(b.category || '') + ' ' + String(b.item || ''))) return false;
    if (isPickup(b)) return false;
    return (Number(b.totalPrice) || 0) > 0;
  }

  /* Returns {id, cents} for a line, or null. Same precedence as cf-real-line-items.js,
     plus the glass width path. */
  function resolve(b) {
    var sku = b.sku == null ? '' : String(b.sku);
    if (sku && /^\d{12,}$/.test(sku)) return { id: Number(sku), cents: null };
    if (sku && typeof window.CF_VARIANT === 'function') {
      var v = window.CF_VARIANT(sku);
      if (v) {
        var c = (typeof window.CF_PRICE === 'function') ? window.CF_PRICE(sku) : null;
        return { id: Number(v), cents: c || null };
      }
    }
    if (b.variantId) return { id: Number(b.variantId), cents: null };
    if (/glass|panel|gate/i.test(String(b.category || '') + ' ' + String(b.item || ''))) {
      var g = glassLookup(b.item);
      if (g) return { id: g.id, cents: g.cents, sku: g.sku };
    }
    return null;
  }

  /* Flat top quotes a "Spigots & Hardware" line at $55.15 - the GLASS spigot price out of
     SPIGOT_PRICES in ullc-calc.js. Flat top posts are the base-plate-and-cap product
     (49838) and do not take a spigot, so nothing is fitted for that $110.30. Pete
     confirmed 2026-08-15: remove it. Recorded as a rule on 2026-08-10 too ("no Spigots &
     Hardware line anywhere" on flat top).

     Flat top ONLY - glass legitimately quotes six core drill spigots at the same price, so
     this is keyed on the calculator, never on the label. */
  function isStraySpigot(b, calc) {
    if (calc !== 'flattop') return false;
    return /spigot/i.test(String(b.category || '')) || /spigot/i.test(String(b.item || ''));
  }

  function correct(q, calc) {
    var changes = [], removedTax = 0, removedSpigot = 0, i;

    for (i = q.breakdown.length - 1; i >= 0; i--) {
      if (isTax(q.breakdown[i])) {
        removedTax += Number(q.breakdown[i].totalPrice) || 0;
        q.breakdown.splice(i, 1);
      } else if (isStraySpigot(q.breakdown[i], calc)) {
        removedSpigot += Number(q.breakdown[i].totalPrice) || 0;
        changes.push('dropped ' + q.breakdown[i].quantity + ' x ' + q.breakdown[i].item);
        q.breakdown.splice(i, 1);
      }
    }

    /* One metro rate for every delivered order. Idempotent: a line already at the rate is
       left alone, so this can run over the same quote repeatedly. */
    q.breakdown.forEach(function (b) {
      if (!isMetroDelivery(b)) return;
      var was = Number(b.unitPrice);
      if (!isFinite(was)) was = Number(b.totalPrice) || 0;
      if (Math.abs(was - METRO_DELIVERY) < 0.005) return;
      b.unitPrice = METRO_DELIVERY;
      b.quantity = 1;
      /* The tier this label advertises no longer exists. */
      if (/orders?\s*(<|under)/i.test(String(b.item || ''))) b.item = 'Perth Metro Delivery';
      changes.push('delivery $' + was.toFixed(2) + ' -> $' + METRO_DELIVERY.toFixed(2));
    });

    q.breakdown.forEach(function (b) {
      if (isNonStock(b)) return;
      var r;
      try { r = resolve(b); } catch (e) { r = null; }
      if (!r) return;

      if (!b.variantId && r.id) {
        b.variantId = r.id;
        if (!b.sku && r.sku) b.sku = r.sku;
        changes.push('resolved ' + b.item);
      }
      if (r.cents) {
        var live = Math.round(r.cents) / 100;
        var was = Number(b.unitPrice) || 0;
        if (Math.abs(live - was) >= 0.005) {
          b.unitPrice = live;
          changes.push(b.item + ' $' + was.toFixed(2) + ' -> $' + live.toFixed(2));
        }
      }
    });

    var total = 0;
    q.breakdown.forEach(function (b) {
      b.totalPrice = Math.round((Number(b.quantity) || 0) * (Number(b.unitPrice) || 0) * 100) / 100;
      total += b.totalPrice;
    });
    var newTotal = Math.round(total * 100) / 100;
    var changed = changes.length > 0 || removedTax > 0 || removedSpigot > 0 || newTotal !== q.grandTotal;
    q.grandTotal = newTotal;

    /* The editor's baseline is keyed on breakdown INDEX, so removing a line invalidates it.
       Drop it and let the editor recapture from the corrected breakdown. */
    if (removedTax > 0 || removedSpigot > 0) { delete q.cfBaseline; delete q.cfAdjustments; }

    return { changed: changed, changes: changes, removedTax: removedTax, removedSpigot: removedSpigot };
  }

  /* Same resolution as the other assets: the highlighted pill wins, and nothing else is
     accepted. Correcting a quote that is not on screen would rewrite prices nobody is
     looking at. */
  function locate() {
    var pill = document.querySelector('.qr-quote-pill.is-active');
    var label = pill ? String(pill.textContent || '').trim() : '';
    if (!label) return null;
    for (var i = 0; i < CALCS.length; i++) {
      var c = CALCS[i], active = null;
      try { active = JSON.parse(localStorage.getItem(c + '_active_quote') || 'null'); } catch (e) {}
      if (active && active.breakdown && active.breakdown.length
          && String(active.quoteName || '').trim() === label) {
        return { key: c + '_active_quote', idx: -1, quote: active, calc: c };
      }
      var arr = [];
      try { arr = JSON.parse(localStorage.getItem(c + '_quotes') || '[]') || []; } catch (e) {}
      for (var j = 0; j < arr.length; j++) {
        var q = arr[j];
        if (!q || !q.breakdown || !q.breakdown.length) continue;
        if (String(q.quoteName || '').trim() === label) return { key: c + '_quotes', idx: j, quote: q, calc: c };
      }
    }
    /* No match yet means the page is still rendering - retry. */
    return null;
  }

  function save(ref) {
    if (ref.idx < 0) { localStorage.setItem(ref.key, JSON.stringify(ref.quote)); return; }
    var arr = JSON.parse(localStorage.getItem(ref.key) || '[]');
    arr[ref.idx] = ref.quote;
    localStorage.setItem(ref.key, JSON.stringify(arr));
  }

  var done = {};
  var tries = 0;

  function run() {
    if (tries++ > 40) return;
    try {
      if (!/quote-review/.test(location.pathname)) return;
      var ref = locate();
      if (!ref) { setTimeout(run, 500); return; }
      var name = String(ref.quote.quoteName || ref.key + ':' + ref.idx);
      if (done[name]) return;

      buildWidthMap().then(function () {
        try {
          if (done[name]) return;
          done[name] = true;
          var res = correct(ref.quote, ref.calc);
          if (!res.changed) { console.log('[cf-quote-truth] ' + name + ' already agrees with Shopify'); return; }
          save(ref);
          console.log('[cf-quote-truth] ' + name + ': ' + res.changes.length + ' correction(s)'
            + (res.removedTax ? ', dropped $' + res.removedTax.toFixed(2) + ' GST line' : '')
            + (res.removedSpigot ? ', dropped $' + res.removedSpigot.toFixed(2) + ' stray spigot line' : '')
            + (res.changes.length ? ' - ' + res.changes.join('; ') : ''));

          /* Rebuild whatever is already on screen from the corrected quote. Re-clicking the
             active pill is what cf-quote-editor.js already treats as a quote switch. */
          var pill = document.querySelector('.qr-quote-pill.is-active');
          if (pill) pill.click();
          else document.dispatchEvent(new CustomEvent('cf:quote-adjusted',
            { detail: { adjustments: [], total: ref.quote.grandTotal } }));
        } catch (e) {
          console.warn('[cf-quote-truth] correction failed', e);
        }
      });
    } catch (e) {
      console.warn('[cf-quote-truth] run failed', e);
      setTimeout(run, 800);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
  setTimeout(run, 900);
  setTimeout(run, 2500);
})();
